<template>
    <section>
        <div class="verifier-chapter-container">
            <button class="verifier-chapter-btn"
                v-for="chapter in chapters"
                v-bind:class="{ 'verifier-chapter-btn__active': chapter === currentChapter }"
                v-on:click="changeChapter(chapter)"
                v-bind:key="chapter"
            >
                {{ $t(`address.contract.chapters.${chapter}`) }}
            </button>
        </div>

        <section v-if="currentChapter === 'source'">
            <verifier-admonition
                v-bind:codeHash="codeHash"
                v-bind:isVerified="isVerified"
                v-bind:isActive="isActive"
                v-bind:compilerName="compilerName"
                v-bind:compilerVersion="compilerVersion"/>

            <section class="verifier-sources" v-show="isLoading || isActive">
                <aside class="verifier-sources__files">
                    <div class="verifier-sources__files__elevator">
                        <verifier-file-list v-if="tree === undefined || tree.length > 0"
                            v-bind:files="tree"
                            v-on:selectFile="selectFile"/>

                        <verifier-file-list
                            v-bind:files="rawFiles"
                            v-on:selectFile="selectRaw"/>
                    </div>
                </aside>

                <div class="verifier-sources__code">
                    <verifier-bytecode-viewer
                        v-if="currentTab.filename === 'raw_data' || currentTab.filename === 'raw_source'"
                        v-bind:source-base64="currentTab.content.base64"
                        v-bind:source-decompiled="currentTab.content.decompiled"
                        v-bind:source-cells="currentTab.content.cells"
                        v-bind:source-hex="currentTab.content.hex"
                        v-bind:hexHash="currentTab.content.hexHash"
                        v-bind:base64Hash="currentTab.content.base64Hash"/>

                    <keep-alive v-else>
                        <verifier-source-code
                            v-bind:fullPath="currentTab.fullPath"
                            v-bind:filename="currentTab.filename"
                            v-bind:content="currentTab.content"/>
                    </keep-alive>
                </div>
            </section>
        </section>

        <address-formats
            v-if="currentChapter === 'formats'"
            v-bind:address="address"
        />

        <tg-wallet-state
            v-if="currentChapter === 'tg_wallet'"
            v-bind:state="tgWalletState"
        />

        <div v-if="['storage', 'abi', 'methods'].includes(currentChapter)" class="contract-chapter">
            <contract-storage
                v-if="currentChapter === 'storage'"
                v-bind:data="contractState.data"
                v-bind:abi="contractAbi && contractAbi.abi"/>

            <contract-abi
                v-if="currentChapter === 'abi'"
                v-bind:abi="contractAbi.abi"
                v-bind:file="contractAbi.file"/>

            <contract-methods
                v-if="currentChapter === 'methods'"
                v-bind:address="address"
                v-bind:abi="contractAbi && contractAbi.abi"/>
        </div>
    </section>
</template>

<script>
import { getVerifiedSourceByAddress } from '~/api/extenderContracts.js';
import { base64ToHex } from '~/utils.js';
import { getAccountStateBoc, getTgWalletState } from '~/api/toncenterV3.js';
import AddressFormats from '~/components/address/Verifier/AddressFormats.vue';
import { preloadHighlight } from '~/lib/highlight.js';
import VerifierSourceCode from './VerifierSourceCode.vue';
import VerifierFileList from './VerifierFileList.vue';
import VerifierAdmonition from './VerifierAdmonition.vue';
import VerifierBytecodeViewer from './VerifierBytecodeViewer.vue';
import TgWalletState from './TgWalletState.vue';

// Chapters arrive one by one as their data does; they still line up in this order:
const CHAPTER_ORDER = ['source', 'formats', 'tg_wallet', 'storage', 'abi', 'methods'];

/**
 * @param  {Array<Object>} sources
 * @return {Object}
 */
const generateFileTree = function createFileTreeFromFileList(sources) {
    // We need flat map of source file references for easier access:
    const flatMap = [];

    // Backup fullPath because we'll change filename prop:
    for (let i = 0; i < sources.length; i += 1) {
        sources[i].fullPath = sources[i].name; // eslint-disable-line no-param-reassign
        sources[i].filename = sources[i].name; // eslint-disable-line no-param-reassign
    }

    const makeTree = (files) => {
        const tree = [];

        for (let i = 0; i < files.length; i += 1) {
            const file = files[i];
            const frags = file.filename.split('/');

            flatMap.push(file);

            if (frags.length === 1) {
                file.isActive = false; // set reactivity on
                tree.push(file);
                continue;
            }

            const directoryName = frags[0];

            // Get all files in the directory:
            const directoryContents = files
                .filter(source => source.filename.startsWith(`${directoryName}/`))
                .map((source) => {
                    const fullPath = source.filename;
                    const filename = fullPath.replace(`${directoryName}/`, '');
                    return { ...source, filename };
                });

            // Push directories in the beginning:
            tree.unshift({
                name: directoryName,
                fullPath: frags.toSpliced(-1).join('/'),
                files: makeTree(directoryContents),
                isCollapsed: !directoryContents.some(source => source.isEntrypoint),
            });

            // As the files are sorted and processing is sequential, we can skip all
            // with names starting with directory name:
            i += (directoryContents.length - 1);
        }

        return tree;
    };

    return { flatMap, treeMap: makeTree(sources) };
};

export default {
    rawInfoPromise: undefined,

    props: {
        isActive: Boolean,
        address: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            sources: [],
            tree: undefined,
            isVerified: undefined,
            rawFiles: [],
            chapters: ['source', 'formats'],
            currentChapter: 'source',
            tgWalletState: null,
            contractState: null,
            contractAbi: null,
            currentTab: {},
            isLoading: true,
            codeHash: undefined,
            compilerName: undefined,
            compilerVersion: undefined,
        };
    },

    created() {
        this.rawFiles = [{
            filename: 'raw_source',
            isActive: false,
            content: {},
            text: this.$t('address.contract.tab_raw_source'),
        }, {
            filename: 'raw_data',
            isActive: false,
            content: {},
            text: this.$t('address.contract.tab_raw_data'),
        }];
        this.loadTgWalletState();
        this.loadContractAbi();
    },

    beforeDestroy() {
        this.$options.rawInfoPromise = undefined;
    },

    watch: {
        isActive: {
            immediate: true,
            // wait until we have address activity info:
            handler() {
                return this.loadData();
            },
        },
    },

    methods: {
        async getVerifiedSource(address) {
            return getVerifiedSourceByAddress(address);
        },

        async loadData() {
            if (this.isActive === undefined) {
                return;
            }

            if (this.isActive === false) {
                this.isLoading = false;
                this.isVerified = false;
                return;
            }

            // The verifier is asked only here, once the tab is open; the
            // highlighter chunks download while it answers:
            preloadHighlight().catch(() => {});

            const response = await this.getVerifiedSource(this.address);

            this.isLoading = false;
            this.isVerified = response.verificationDate !== null;
            this.codeHash = response.codeHash;

            this.compilerName = response?.compiler || 'unknown';
            this.compilerVersion = response?.compilerSettings?.version;

            if (!response.files.length) {
                this.tree = [];
                this.selectRaw(this.rawFiles[0]);
                return;
            }

            const { flatMap, treeMap } = generateFileTree(response.files);

            // root level should have reverse order (files first, directories last):
            treeMap.sort((_, b) => (b.files ? -1 : 1));
            treeMap.sort((a, _) => (a.is_entrypoint ? -1 : 1));

            this.tree = treeMap;
            this.sources = flatMap;

            // The entrypoint opens first; of its copies on every tree level the list shows the deepest one:
            this.selectFile(flatMap.find(file => file.isEntrypoint && !file.filename.includes('/')) || flatMap[0]);
        },

        selectFile(selectedFile) {
            this.sources.concat(this.rawFiles).forEach((tab) => {
                tab.isActive = Object.is(tab, selectedFile); // eslint-disable-line no-param-reassign
            });

            this.currentTab = selectedFile;
        },

        selectRaw(rawSource) {
            this.selectFile(rawSource);

            this.loadRawInfo().then((contract) => {
                this.currentTab.content = rawSource.filename === 'raw_data'
                    ? contract?.data
                    : contract?.code;
            });
        },

        async loadRawInfo() {
            if (!this.$options.rawInfoPromise) {
                this.$options.rawInfoPromise = getAccountStateBoc(this.address);
            }

            // Toncenter gives the hashes in base64 only:
            return this.$options.rawInfoPromise.then(({ code_boc: code, data_boc: data, code_hash: codeHash, data_hash: dataHash } = {}) => ({
                code: {
                    base64: code,
                    hex: base64ToHex(code),
                    hexHash: base64ToHex(codeHash),
                    base64Hash: codeHash,
                },
                data: {
                    base64: data,
                    hex: base64ToHex(data),
                    hexHash: base64ToHex(dataHash),
                    base64Hash: dataHash,
                },
            }));
        },

        // Telegram wallets get a chapter of their own: the signing key, seqno
        // and subwallet id are read from the data cell, nowhere else.
        async loadTgWalletState() {
            try {
                const state = await getTgWalletState(this.address);
                if (state) {
                    this.tgWalletState = state;
                    this.addChapter('tg_wallet');
                }
            } catch (error) {
                console.error(error);
            }
        },

        // Storage, ABI and methods of a deployed contract. Only the code hash
        // is matched here (a small hash map); the decoders load with the chapter.
        async loadContractAbi() {
            try {
                const account = await getAccountStateBoc(this.address);
                if (!account?.code_boc) {
                    return;
                }

                // Lazy too: the catalog module knows every contract file, which the main bundle should not.
                const { contractCodeHash, findAbiByCodeHash } = await import(/* webpackChunkName: "abi-catalog" */ '~/abi/catalog.js');
                const found = await findAbiByCodeHash(contractCodeHash(account.code_hash, account.code_boc));

                // Frozen: Vue must not walk a whole ABI to make it reactive.
                this.contractState = Object.freeze({ data: account.data_boc });
                this.contractAbi = found && Object.freeze(found);

                if (account.data_boc) this.addChapter('storage');
                if (found) this.addChapter('abi');
                this.addChapter('methods');
            } catch (error) {
                console.error(error);
            }
        },

        addChapter(chapter) {
            this.chapters.push(chapter);
            this.chapters.sort((a, b) => CHAPTER_ORDER.indexOf(a) - CHAPTER_ORDER.indexOf(b));
        },

        async changeChapter(chapter) {
            this.currentChapter = chapter;
        },
    },

    components: {
        VerifierSourceCode,
        VerifierBytecodeViewer,
        VerifierAdmonition,
        VerifierFileList,
        AddressFormats,
        TgWalletState,
        ContractStorage: () => import(/* webpackChunkName: "contract-abi" */ '~/components/abi/ContractStorage.vue'),
        ContractAbi: () => import(/* webpackChunkName: "contract-abi" */ '~/components/abi/ContractAbi.vue'),
        ContractMethods: () => import(/* webpackChunkName: "contract-abi" */ '~/components/abi/ContractMethods.vue'),
    },
};
</script>

<style lang="scss">
.verifier-chapter-container {
    width: 100%;
    overflow-x: scroll;
    overflow-y: hidden;
    display: inline-flex;
    padding: 0 12px 0 0;
    margin: 10px 0;
    position: relative;
    box-sizing: border-box;
    scrollbar-color: transparent transparent;
}

@media screen and (min-height: 600px) {
    .verifier-chapter-container {
        display: flex;
        margin: 16px 0 10px;
        overflow-x: auto;
        overflow-y: auto;
    }
}

.verifier-chapter-btn {
    padding: 0 14px;
    line-height: 33px;
    color: var(--body-muted-text-color);
    border: 1px solid var(--card-border-color);
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    background: var(--tx-table-thead-alt-background);
    transition: .1s background;
    margin-right: 7px;
    white-space: nowrap;
    user-select: none;

    &__active {
        border-color: var(--filter-item-active);
        color: var(--body-text-color);
        background: var(--chapter-active-background);
    }

    &:first-child {
        margin-left: 12px;
    }

    &:hover:not(.verifier-chapter-btn__active) {
        background: var(--button-options-background-hover);
    }
}

.verifier-sources {
    display: flex;
    flex-direction: row;
    &__files {
        margin: 0 0 12px 12px;
        width: 240px;
        flex-shrink: 0;
        &__elevator {
            position: sticky;
            top: 12px;
        }
    }
    &__code {
        flex-grow: 1;
        overflow: auto;
        margin: 0 12px 12px 12px;
    }
}

.contract-chapter {
    margin: 0 12px 12px;
}

.source-viewer-code__pre {
    display: flex;
    flex-direction: row;
    overflow: auto;
}

.hljs-hack-lines {
    padding: 0 10px 0 0;
    text-align: right;
    color: var(--body-muted-text-color);
}

.hljs-hack-code {
    padding: 0 10px 0 4px;
}

@media all and (max-width: 480px) {
    .verifier-sources {
        flex-direction: column;
        &__files {
            width: unset;
            margin-right: 12px;
        }
        &__code {
            margin-top: 0;
        }
    }
}
</style>
